import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Plus, Trash2, X, Pencil } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function Customers() {
  const { user } = useAuth();
  const canEdit = ["admin", "sales", "account"].includes(user?.role);
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [show, setShow] = useState(false);

  const load = async () => {
    const r = await api.get("/customers");
    // Alphabetise the customer list — every party lookup surface (list,
    // dropdowns, statement links) now shares a canonical A→Z order.
    setItems([...r.data].sort((a, b) => (a.name || "").localeCompare(b.name || "", "en", { sensitivity: "base" })));
  };
  useEffect(() => { load().catch(() => {}); }, []);

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Master Data</div>
          <h1 className="text-3xl font-bold tracking-tight text-zinc-950">Customers</h1>
        </div>
        {canEdit && <button data-testid="new-customer-btn" onClick={() => { setEditing(null); setShow(true); }} className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 text-sm uppercase tracking-wider font-medium flex items-center gap-2"><Plus size={16} /> New Customer</button>}
      </div>

      <div className="bg-white border border-zinc-300 overflow-x-auto" data-testid="customers-table">
        <table className="w-full text-sm border-collapse">
          <thead className="bg-zinc-100 text-zinc-600 uppercase text-[10px] font-mono tracking-wider">
            <tr>
              <th className="text-left px-3 py-2 border-b border-zinc-300">Name</th>
              <th className="text-left px-3 py-2 border-b border-zinc-300">Company</th>
              <th className="text-left px-3 py-2 border-b border-zinc-300">Phone</th>
              <th className="text-left px-3 py-2 border-b border-zinc-300">Email</th>
              <th className="text-left px-3 py-2 border-b border-zinc-300">GST</th>
              <th className="text-right px-3 py-2 border-b border-zinc-300">Cr. Days</th>
              <th className="text-right px-3 py-2 border-b border-zinc-300">Opening ₹</th>
              <th className="text-left px-3 py-2 border-b border-zinc-300">Address</th>
              {canEdit && <th className="border-b border-zinc-300"></th>}
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={9} className="text-center text-zinc-500 py-12 text-sm">No customers yet. Add your first buyer.</td></tr>
            ) : items.map((c) => (
              <tr key={c.id} className="border-b border-zinc-200 hover:bg-zinc-50" data-testid={`customer-row-${c.id}`}>
                <td className="px-3 py-2 font-semibold text-zinc-900">{c.name}</td>
                <td className="px-3 py-2">{c.company || "—"}</td>
                <td className="px-3 py-2 font-mono text-xs">{c.phone || "—"}</td>
                <td className="px-3 py-2 font-mono text-xs">{c.email || "—"}</td>
                <td className="px-3 py-2 font-mono text-xs">{c.gst || "—"}</td>
                <td className="px-3 py-2 font-mono text-xs text-right">{c.default_credit_days ?? 30}</td>
                <td className={`px-3 py-2 font-mono text-xs text-right ${(c.opening_balance || 0) > 0 ? "text-orange-800 font-semibold" : (c.opening_balance || 0) < 0 ? "text-blue-800 font-semibold" : "text-zinc-400"}`}>
                  {(c.opening_balance || 0) === 0 ? "—" : (c.opening_balance > 0 ? "" : "-") + Math.abs(c.opening_balance).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  {c.opening_balance_date && <div className="text-[9px] text-zinc-500 font-normal">as of {c.opening_balance_date}</div>}
                </td>
                <td className="px-3 py-2 text-xs max-w-xs truncate">{c.address || "—"}</td>
                {canEdit && (
                  <td className="px-3 py-2 text-right space-x-2 whitespace-nowrap">
                    <button data-testid={`edit-customer-${c.id}`} onClick={() => { setEditing(c); setShow(true); }} className="text-zinc-600 hover:text-zinc-900"><Pencil size={14} /></button>
                    {user?.role === "admin" && (
                      <button data-testid={`delete-customer-${c.id}`} onClick={async () => {
                        if (!window.confirm(`Delete ${c.name}?`)) return;
                        try { await api.delete(`/customers/${c.id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
                      }} className="text-red-600 hover:text-red-800"><Trash2 size={14} /></button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {show && <CustomerForm editing={editing} onClose={() => setShow(false)} onSaved={() => { setShow(false); load(); }} />}
    </div>
  );
}

export function CustomerForm({ editing, onClose, onSaved }) {
  // Merge existing document with sensible defaults so older customers
  // (created before the accounting fields existed) still populate the form
  // instead of showing blank inputs that would post `undefined`.
  const [form, setForm] = useState(() => ({
    name: "", company: "", phone: "", email: "", gst: "", address: "",
    default_credit_days: 30, opening_balance: 0, opening_balance_date: "",
    ...(editing || {}),
  }));
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      // Coerce number inputs so backend doesn't reject strings.
      const payload = { ...form,
        default_credit_days: Number(form.default_credit_days) || 30,
        opening_balance: Number(form.opening_balance) || 0,
      };
      if (editing) await api.put(`/customers/${editing.id}`, payload);
      else await api.post("/customers", payload);
      toast.success(editing ? "Updated" : "Created");
      onSaved();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const f = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const cls = "w-full h-9 px-2 text-sm border border-zinc-300 bg-white font-mono";
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center overflow-y-auto py-10" data-testid="customer-form-modal">
      <div className="bg-white border border-zinc-300 w-full max-w-2xl">
        <div className="flex items-center justify-between border-b border-zinc-300 px-6 py-4">
          <h2 className="text-xl font-semibold">{editing ? "Edit" : "New"} Customer</h2>
          <button onClick={onClose}><X /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-[10px] font-semibold uppercase mb-1 block">Name *</label><input data-testid="cust-name" required value={form.name} onChange={f("name")} className={cls} /></div>
            <div><label className="text-[10px] font-semibold uppercase mb-1 block">Company</label><input data-testid="cust-company" value={form.company} onChange={f("company")} className={cls} /></div>
            <div><label className="text-[10px] font-semibold uppercase mb-1 block">Phone</label><input data-testid="cust-phone" value={form.phone} onChange={f("phone")} className={cls} /></div>
            <div><label className="text-[10px] font-semibold uppercase mb-1 block">Email</label><input data-testid="cust-email" type="email" value={form.email} onChange={f("email")} className={cls} /></div>
            <div><label className="text-[10px] font-semibold uppercase mb-1 block">GST No.</label><input data-testid="cust-gst" value={form.gst} onChange={f("gst")} className={cls} /></div>
          </div>
          <div>
            <label className="text-[10px] font-semibold uppercase mb-1 block">Address</label>
            <textarea data-testid="cust-address" value={form.address} onChange={f("address")} rows={2} className="w-full p-2 text-sm border border-zinc-300 bg-white font-mono" />
          </div>
          <div className="border-t border-zinc-200 pt-3">
            <div className="text-[10px] font-mono uppercase tracking-widest text-orange-700 mb-2">
              Accounting — opening figures &amp; credit terms
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-[10px] font-semibold uppercase mb-1 block">Credit Days</label>
                <input data-testid="cust-credit-days" type="number" min="0" max="365" value={form.default_credit_days ?? 30}
                  onChange={f("default_credit_days")} className={cls} />
              </div>
              <div>
                <label className="text-[10px] font-semibold uppercase mb-1 block" title="Positive = customer owes us on go-live; Negative = customer has credit">
                  Opening Balance ₹ <span className="text-zinc-400">(+Dr / −Cr)</span>
                </label>
                <input data-testid="cust-opening-balance" type="number" step="0.01" value={form.opening_balance ?? 0}
                  onChange={f("opening_balance")} className={cls} placeholder="0.00" />
              </div>
              <div>
                <label className="text-[10px] font-semibold uppercase mb-1 block">Opening Date</label>
                <input data-testid="cust-opening-date" type="date" value={form.opening_balance_date || ""}
                  onChange={f("opening_balance_date")} className={cls} />
              </div>
            </div>
            <p className="text-[10px] font-mono text-zinc-500 mt-2">
              Opening balance is a one-time carry-forward from your legacy books. It appears as the first row on the party's statement and is aged into <b>60+ days</b> on the dashboard.
            </p>
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t border-zinc-200">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm uppercase border border-zinc-300 bg-white">Cancel</button>
            <button data-testid="save-customer-btn" type="submit" disabled={busy} className="px-5 py-2 text-sm uppercase bg-orange-600 hover:bg-orange-700 text-white disabled:opacity-60">{busy ? "..." : "Save"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
